import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Search, UserPlus, ShieldCheck, CalendarRange, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Build YYYY-MM-DD for a date N days before today (IST tolerant via local date).
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const today = () => isoDate(new Date());
const minusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDate(d);
};

export default function Patients() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const initialFrom = params.get("seen_from") || "";
  const initialTo = params.get("seen_to") || "";
  const [q, setQ] = useState(initialQ);
  const [seenFrom, setSeenFrom] = useState(initialFrom);
  const [seenTo, setSeenTo] = useState(initialTo);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = async (search, from, to) => {
    setLoading(true);
    try {
      const qp = { q: search, limit: 200 };
      if (from) qp.seen_from = from;
      if (to) qp.seen_to = to;
      const { data } = await api.get("/patients", { params: qp });
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(initialQ, initialFrom, initialTo); /* eslint-disable-next-line */ }, []);

  const applyFilters = (overrides = {}) => {
    const next = {
      q: overrides.q ?? q,
      seen_from: overrides.seen_from ?? seenFrom,
      seen_to: overrides.seen_to ?? seenTo,
    };
    // Sync URL — but drop empty params for clean URLs
    const cleaned = Object.fromEntries(Object.entries(next).filter(([, v]) => v));
    setParams(cleaned);
    fetchData(next.q, next.seen_from, next.seen_to);
  };

  const clearDates = () => {
    setSeenFrom("");
    setSeenTo("");
    applyFilters({ seen_from: "", seen_to: "" });
  };

  const presetToday = () => {
    const d = today();
    setSeenFrom(d); setSeenTo(d);
    applyFilters({ seen_from: d, seen_to: d });
  };
  const presetYesterday = () => {
    const d = minusDays(1);
    setSeenFrom(d); setSeenTo(d);
    applyFilters({ seen_from: d, seen_to: d });
  };
  const preset7days = () => {
    const from = minusDays(6); const to = today();
    setSeenFrom(from); setSeenTo(to);
    applyFilters({ seen_from: from, seen_to: to });
  };
  const preset30days = () => {
    const from = minusDays(29); const to = today();
    setSeenFrom(from); setSeenTo(to);
    applyFilters({ seen_from: from, seen_to: to });
  };

  const dateFilterActive = !!(seenFrom || seenTo);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">Patient registry</div>
          <h1 className="font-heading text-3xl font-semibold">
            Patients <span className="ml-2 text-sm font-body text-ink-muted">({total})</span>
            {dateFilterActive && (
              <span className="ml-3 align-middle inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/5 px-2.5 py-0.5 text-xs font-medium text-brand">
                <CalendarRange size={11} /> Seen {seenFrom || "…"} → {seenTo || "…"}
              </span>
            )}
          </h1>
        </div>
        <Link to="/app/patients/new">
          <Button className="bg-brand hover:bg-brand-700" data-testid="patients-new-btn"><UserPlus size={16} className="mr-2" /> New Patient</Button>
        </Link>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
            <Input
              data-testid="patients-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { applyFilters({ q }); } }}
              placeholder="Search by UHID, name, mobile, ABHA…" className="pl-9"
            />
          </div>
          <Button data-testid="patients-search-btn" onClick={() => applyFilters({ q })} variant="outline">Search</Button>
        </div>

        {/* Date-range filter for past patients seen */}
        <div className="rounded-md border border-border bg-secondary/30 p-3">
          <div className="mb-2 flex items-center gap-2">
            <CalendarRange size={14} className="text-brand" />
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">Past patients seen</span>
            <span className="text-[11px] text-ink-muted">— filter by visit/consultation date</span>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <Label htmlFor="seen-from" className="text-[11px]">From</Label>
              <Input
                id="seen-from"
                type="date"
                value={seenFrom}
                max={seenTo || undefined}
                onChange={(e) => setSeenFrom(e.target.value)}
                data-testid="patients-seen-from"
                className="w-[150px]"
              />
            </div>
            <div className="flex flex-col">
              <Label htmlFor="seen-to" className="text-[11px]">To</Label>
              <Input
                id="seen-to"
                type="date"
                value={seenTo}
                min={seenFrom || undefined}
                onChange={(e) => setSeenTo(e.target.value)}
                data-testid="patients-seen-to"
                className="w-[150px]"
              />
            </div>
            <Button
              onClick={() => applyFilters({})}
              className="bg-brand hover:bg-brand-700"
              data-testid="patients-seen-apply"
            >Apply</Button>
            {dateFilterActive && (
              <Button onClick={clearDates} variant="ghost" size="sm" data-testid="patients-seen-clear">
                <X size={12} className="mr-1" /> Clear
              </Button>
            )}
            <div className="ml-auto flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={presetToday} data-testid="patients-preset-today">Today</Button>
              <Button size="sm" variant="outline" onClick={presetYesterday} data-testid="patients-preset-yesterday">Yesterday</Button>
              <Button size="sm" variant="outline" onClick={preset7days} data-testid="patients-preset-7d">Last 7 days</Button>
              <Button size="sm" variant="outline" onClick={preset30days} data-testid="patients-preset-30d">Last 30 days</Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3 text-left">UHID</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Gender</th>
                <th className="px-4 py-3 text-left">Mobile</th>
                <th className="px-4 py-3 text-left">ABHA</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (<tr><td colSpan="7" className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>)}
              {!loading && items.length === 0 && (<tr><td colSpan="7" className="px-4 py-10 text-center text-ink-muted">No patients found.</td></tr>)}
              {items.map(p => (
                <tr key={p.id} className="row-enter hover:bg-secondary/40" data-testid={`patient-row-${p.uhid}`}>
                  <td className="px-4 py-3 font-mono text-xs">{p.uhid}</td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 capitalize">{p.gender}</td>
                  <td className="px-4 py-3">{p.mobile}</td>
                  <td className="px-4 py-3">
                    {p.abha_number ? (
                      <Badge variant="outline" className="border-leaf/40 bg-leaf-50 text-leaf-700"><ShieldCheck size={11} className="mr-1" />Linked</Badge>
                    ) : <span className="text-ink-subtle text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 capitalize">{p.category}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/app/patients/${p.id}`}>
                      <Button size="sm" variant="outline" data-testid={`patient-open-${p.uhid}`}>Open</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
